import React from "react";
import styles from "./styles.module.scss";
import dropdownArrow from "app/images/down-arrow.svg";
import DropdownButton from "app/components/DropdownButton";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { FacilityModel } from "app/models/scheduling/FacilityModel";
import { setBookSession } from "app/actions/scheduling/bookSession";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import { hasFacilityLogo } from "app/components/PublicProfiles/Facility/facilityBranding";

interface Props {
  facilities: FacilityModel[];
  /**
   * Drop the white card, padding and bottom rule this control carries for the
   * old layout. Inside the redesigned facility row that chrome is the row's
   * job, and keeping it produced a card nested inside a card.
   */
  embedded?: boolean;
  /**
   * A second line under the facility name, inside the control. Frame 526:28686
   * draws the name and the facility's counts as one two-line block beside the
   * logo, so the line has to live in here rather than under the whole control.
   */
  subtitle?: React.ReactNode;
}

/**
 * The logo to show for a facility, or null when it has none and the initial
 * stands in. Prefers the public logo — the one the landing page and the booking
 * pages use — and treats the API's placeholder as absent, which `logo_url` alone
 * did not.
 */
const facilityLogoUrl = (facility: any): string | null => {
  if (hasFacilityLogo(facility?.public_logo_url)) {
    return facility.public_logo_url;
  }
  return hasFacilityLogo(facility?.logo_url) ? facility.logo_url : null;
};

const SelectFacility: React.FC<Props> = ({
  facilities,
  embedded = false,
  subtitle,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { facility } = useSelector((state: RootState) => state.bookSession);

  // Entering the screen clears any in-progress selection, but that belongs to
  // the screen rather than to this control: the dropdown is rendered in more
  // than one place now, and resetting on *mount* meant that moving it — as
  // happens when the coach picker appears — wiped the facility that had just
  // been chosen. AllCoachesPrivateSession owns the reset.

  const setFacilityHandle = (value) => {
    dispatch(
      setBookSession({
        facility: value,
      }),
    );
  };

  if (facilities.length === 0) {
    history.push("/");
    toast.error(t("An unexpected error has occurred."));
    return undefined;
  }

  return (
    <div
      className={cls(styles.facility_selector_container, {
        [styles.facility_selector_embedded]: embedded,
      })}
    >
      <DropdownButton
        btnClassName={styles.dropdown_wrapper}
        // CFTB-3428: turns the arrow over while the facility list is open.
        btnOpenClassName={styles.dropdown_wrapper_open}
        dropdownWrapperClass={styles.dropdown_classname}
        btnContent={
          <div className={styles.facility_dropdown_wrapper}>
            {facilityLogoUrl(facility) ? (
              <img
                src={facilityLogoUrl(facility)}
                alt="facility_logo"
                className={styles.facility_logo}
              />
            ) : (
              <p className={styles.facility_static_logo}>
                {facility?.name?.charAt(0).toUpperCase()}
              </p>
            )}
            {/* Flat when there is no second line, so the layout every other
                screen using this control gets is left exactly as it was. */}
            {subtitle ? (
              <div className={styles.facility_identity_text}>
                <div className={styles.facility_name_row}>
                  <p className={styles.facility_name}>{facility?.name}</p>
                  <img src={dropdownArrow} alt="dropdown_arrow" />
                </div>
                <div className={styles.facility_subtitle}>{subtitle}</div>
              </div>
            ) : (
              <>
                <p className={styles.facility_name}>{facility?.name}</p>
                <img src={dropdownArrow} alt="dropdown_arrow" />
              </>
            )}
          </div>
        }
        closeOnClick
      >
        <div className={styles.options_wrapper}>
          {facilities?.length > 0 &&
            facilities?.map((facilityOpt) => (
              <div
                key={facilityOpt.id}
                className={cls(styles.facility_option, {
                  [styles.selected_option]: facilityOpt?.id === facility?.id,
                })}
                onClick={() => setFacilityHandle(facilityOpt)}
              >
                {facilityLogoUrl(facilityOpt) ? (
                  <img
                    src={facilityLogoUrl(facilityOpt)}
                    alt={`${facilityOpt.name} logo`}
                    className={styles.facility_logo_option}
                  />
                ) : (
                  <p className={styles.facility_static_logo}>
                    {facilityOpt?.name?.charAt(0).toUpperCase()}
                  </p>
                )}
                <span>{facilityOpt.name}</span>
              </div>
            ))}
        </div>
      </DropdownButton>
    </div>
  );
};

export default SelectFacility;
